import React, { useState } from "react";
import {
  FolderIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ArrowUpTrayIcon,
  CalendarIcon,
  ChartBarIcon,
  ClockIcon,
  ChartBarSquareIcon,
  MapPinIcon,
} from "@heroicons/react/24/outline";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { useFetch } from "../../hooks/useCallApi";
import { complaintsAPI } from "../../services/api/admin/complaintsApi";
import toast from 'react-hot-toast';

const renderPercentageLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
}) => {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) / 2;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="#FFFFFF"
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={11}
      fontWeight="bold"
    >
      {(percent * 100).toFixed(0)}%
    </text>
  );
};

const ComplaintsOverview = ({ showDetails, toggleDetails }) => {
  const [isExporting, setIsExporting] = useState({
    complaints: false,
    categories: false,
    status: false,
    priority: false,
    location: false
  });

  const { data: statisticsData, isLoading } = useFetch(
    ['complaints-statistics'],
    (api) => api.admin.complaints.getStatistics(),
    {
      onSuccess: () => {},
      refetchOnWindowFocus: false
    }
  );

  const overview = statisticsData?.data?.overview || {};
  
  const complaintsOverTimeData = overview.complaints_over_time?.map(item => ({
    year: item.year.toString(),
    received: item.received,
    resolved: parseInt(item.resolved) || 0
  })) || [];

  const complaintsByCategory = overview.by_category?.map(item => ({
    type: item.category,
    count: item.total,
    percentage: item.percentage,
    color: getCategoryColor(item.category_id)
  })) || [];

  const complaintsByStatus = overview.by_status?.map(item => ({
    status: item.status.charAt(0).toUpperCase() + item.status.slice(1),
    count: item.total,
    percentage: item.percentage,
    color: getStatusColor(item.status)
  })) || [];

  const complaintsByPriority = overview.by_priority?.map(item => ({
    priority: item.priority.charAt(0).toUpperCase() + item.priority.slice(1),
    count: item.total,
    percentage: item.percentage,
    color: getPriorityColor(item.priority)
  })) || [];

  const locationData = overview.by_location?.map(item => ({
    location: item.purok,
    count: item.total,
    percentage: item.percentage,
    color: getLocationColor(item.purok)
  })) || [];

  function getCategoryColor(categoryId) {
    const colors = {
      1: "#DFB41F",
      2: "#7A808D",
    };
    return colors[categoryId] || "#2563EB";
  }

  function getStatusColor(status) {
    const colors = {
      submitted: "#BD9816",
      resolved: "#16A34F",
      closed: "#E7BCF0",
      pending: "#BD9816",
      in_progress: "#1482CC",
      dismissed: "#E7BCF0"
    };
    return colors[status] || "#6B7280";
  }

  function getPriorityColor(priority) {
    const colors = {
      low: "#6B7280",
      medium: "#DBCA72",
      high: "#EA580C",
      urgent: "#F67681"
    };
    return colors[priority] || "#6B7280";
  }

  function getLocationColor(purok) {
    const colors = [
      "#00497A", "#1482CC", "#F67681", "#1482CC",
      "#DBCA72", "#EA580C", "#1EA550", "#A61B1B"
    ];
    const index = (purok?.length || 0) % colors.length;
    return colors[index];
  }

  const handleExport = async (type) => {
    try {
      setIsExporting(prev => ({ ...prev, [type]: true }));

      const exportParams = {
        export: true,
        overview: type 
      };

      const response = await complaintsAPI.export(exportParams);
      
      if (response.data) {
        const blob = new Blob([response.data], { 
          type: response.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        const contentDisposition = response.headers['content-disposition'];
        let filename = `${type}_export.xlsx`;
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '');
          }
        }
        
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        const typeName = type.charAt(0).toUpperCase() + type.slice(1);
        toast.success(`${typeName} export completed successfully`);
      }
    } catch (error) {
      console.error('Export failed:', error);
      const typeName = type.charAt(0).toUpperCase() + type.slice(1);
      toast.error(`Failed to export ${typeName} data`);
    } finally {
      setIsExporting(prev => ({ ...prev, [type]: false }));
    }
  };

  const DonutCard = ({ title, icon, bgColor, type, children }) => (
    <div className={`${bgColor} rounded-lg p-5`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center">
          <div
            className="rounded-lg w-10 h-10 flex items-center justify-center flex-shrink-0 mr-3"
            style={{ backgroundColor: `${bgColor.replace('bg-[', '').replace(']', '')}99` }}
          >
            {React.cloneElement(icon, {
              className: "w-6 h-6",
              style: { color: icon.props.style?.color || '#2563EB' }
            })}
          </div>
          <h4 className="text-base font-semibold text-gray-900 leading-tight">{title}</h4>
        </div>
        <button
          onClick={() => handleExport(type)}
          disabled={isExporting[type]}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ArrowUpTrayIcon className="w-3.5 h-3.5" />
          {isExporting[type] ? 'Exporting...' : 'Export'}
        </button>
      </div>
      {children}
    </div>
  );

  const ChartContainer = ({ data, label }) => {
    const total = data.reduce((sum, item) => sum + item.count, 0);
    
    return (
      <div className="h-[200px] relative">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              innerRadius={55} 
              outerRadius={90}
              paddingAngle={0}
              dataKey="count"
              stroke="none"
              label={renderPercentageLabel}
              labelLine={false}
            >
              {data.map((entry, index) => (
                <Cell key={index} fill={entry.color} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <span className="text-lg font-bold text-gray-800">{total}</span>
        </div>
      </div>
    );
  };

  const Legend = ({ items }) => (
    <div className="flex flex-wrap justify-center gap-3 mt-4">
      {items.map((item) => (
        <div
          key={item.label || item.location || item.priority || item.status}
          className="flex items-center gap-1.5 text-[11px] text-gray-600 font-semibold"
        >
          <div
            className="w-3 h-3 rounded-[3px]" 
            style={{ backgroundColor: item.color }}
          />
          {item.label || item.location || item.priority || item.status}
        </div>
      ))}
    </div>
  );

  if (isLoading) {
    return (
      <div className="mb-8">
        <div className="bg-white rounded-lg shadow p-8">
          <div className="flex justify-center items-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-8">
      <div className="bg-white rounded-lg shadow mb-6">
        <button
          onClick={toggleDetails}
          className="w-full px-6 py-4 flex items-center justify-between text-left transition-all duration-200 rounded-lg hover:bg-gray-50"
        >
          <div className="flex items-center">
            <div className="p-2 bg-white border border-gray-100 rounded-xl shadow-sm mr-3">
              <FolderIcon className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h4 className="text-lg font-semibold text-gray-900">Complaints Overview</h4>
              <p className="text-sm text-gray-600">Track complaints trends, resolution rates, and category breakdown</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {showDetails ? (
              <ChevronUpIcon className="w-5 h-5 text-gray-600" />
            ) : (
              <ChevronDownIcon className="w-5 h-5 text-gray-600" />
            )}
          </div>
        </button>

        {/* Content */}
        {showDetails && (
          <div className="space-y-6 p-4 animate-in slide-in-from-top duration-300">
            {/* OVERVIEW CARDS */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Complaints Over Time */}
              <div className="lg:col-span-4 bg-[#EFF6FF] rounded-lg p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center">
                    <div className="rounded-lg w-10 h-10 flex items-center justify-center flex-shrink-0 mr-3 bg-[#EFF6FF] bg-opacity-60">
                      <CalendarIcon className="w-6 h-6" style={{ color: '#2563EB' }} />
                    </div>
                    <h4 className="text-base font-semibold text-gray-900 leading-tight">Complaints Over Time</h4>
                  </div>
                  <button
                    onClick={() => handleExport('complaints')}
                    disabled={isExporting.complaints}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ArrowUpTrayIcon className="w-3.5 h-3.5" />
                    {isExporting.complaints ? 'Exporting...' : 'Export'}
                  </button>
                </div>
                
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-400 font-semibold text-[11px] uppercase tracking-wider">
                      <th className="pb-3">Period</th>
                      <th className="pb-3 text-center">Received</th>
                      <th className="pb-3 text-right">Resolved</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-blue-100">
                    {complaintsOverTimeData.length > 0 ? (
                      complaintsOverTimeData.map((item, index) => (
                        <tr key={index}>
                          <td className="py-3 text-gray-500 font-medium text-sm">{item.year}</td>
                          <td className="py-3 text-center font-bold" style={{ color: '#2563EB' }}>
                            {item.received}
                          </td>
                          <td className="py-3 text-right font-bold" style={{ color: '#2563EB' }}>
                            {item.resolved}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="3" className="py-4 text-center text-gray-500">
                          No data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Category  */}
              <div className="lg:col-span-8 bg-[#F0FDF4] rounded-lg p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center">
                    <div className="rounded-lg w-10 h-10 flex items-center justify-center flex-shrink-0 mr-3 bg-[#F0FDF4] bg-opacity-60">
                      <ChartBarIcon className="w-6 h-6" style={{ color: '#16A34F' }} />
                    </div>
                    <h4 className="text-base font-semibold text-gray-900 leading-tight">Category</h4>
                  </div>
                  <button
                    onClick={() => handleExport('categories')}
                    disabled={isExporting.categories}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ArrowUpTrayIcon className="w-3.5 h-3.5" />
                    {isExporting.categories ? 'Exporting...' : 'Export'}
                  </button>
                </div>
                
                <div className="space-y-4">
                  {complaintsByCategory.length > 0 ? (
                    complaintsByCategory.map((item) => (
                      <div key={item.type} className="mb-4 last:mb-0">
                        <div className="flex justify-between text-[13px] mb-1.5 font-medium">
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-full" 
                              style={{ backgroundColor: item.color }}
                            />
                            <span className="text-gray-600 capitalize text-sm">{item.type}</span>
                          </div>
                          <span className="text-gray-500 text-sm">
                            {item.count}{" "}
                            <span className="text-gray-400 font-normal">
                              ({item.percentage?.toFixed(1)}%)
                            </span>
                          </span>
                        </div>
                        <div className="w-full h-[7px] bg-gray-200 rounded-full overflow-hidden">
                          <div
                            style={{ 
                              backgroundColor: item.color,
                              width: `${item.percentage || 0}%`
                            }}
                            className="h-full"
                          />
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center text-gray-500 py-4">
                      No category data available
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* STATUS, PRIORITY, LOCATION */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Status Card  */}
              <DonutCard
                title="Status"
                type="status"
                icon={<ClockIcon className="w-5 h-5" style={{ color: '#8A38F5' }} />}
                bgColor="bg-[#C993F91A]"
              >
                <ChartContainer 
                  data={complaintsByStatus.map(item => ({
                    label: item.status,
                    count: item.count,
                    color: item.color
                  }))} 
                  label="Status" 
                />
                <Legend 
                  items={complaintsByStatus.map(item => ({
                    label: item.status,
                    color: item.color
                  }))} 
                />
              </DonutCard>

              {/* Priority Card */}
              <DonutCard
                title="Priority"
                type="priority"
                icon={<ChartBarSquareIcon className="w-5 h-5" style={{ color: '#DC2626' }} />}
                bgColor="bg-[#FF86861A]"
              >
                <ChartContainer 
                  data={complaintsByPriority.map(item => ({
                    label: item.priority,
                    count: item.count,
                    color: item.color
                  }))} 
                  label="Priority" 
                />
                <Legend 
                  items={complaintsByPriority.map(item => ({
                    label: item.priority,
                    color: item.color
                  }))} 
                />
              </DonutCard>

              {/* Location Card*/}
              <DonutCard
                title="Location"
                type="location"
                icon={<MapPinIcon className="w-5 h-5" style={{ color: '#DFB41F' }} />}
                bgColor="bg-[#FFF7DA]"
              >
                <ChartContainer 
                  data={locationData.map(item => ({
                    label: item.location,
                    count: item.count,
                    color: item.color
                  }))} 
                  label="Location" 
                />
                {locationData.length > 0 && (
                  <div className="flex flex-col gap-3 mt-4 px-2">
                    <div className="flex flex-wrap justify-center gap-4">
                      {locationData.slice(0, Math.ceil(locationData.length/2)).map((item) => (
                        <div
                          key={item.location}
                          className="flex items-center gap-1.5 text-[10px] text-gray-600 font-medium whitespace-nowrap"
                        >
                          <div
                            className="w-2.5 h-2.5 rounded-[2px]"
                            style={{ backgroundColor: item.color }}
                          />
                          {item.location}
                        </div>
                      ))}
                    </div>
                    <div className="flex flex-wrap justify-center gap-4">
                      {locationData.slice(Math.ceil(locationData.length/2)).map((item) => (
                        <div
                          key={item.location}
                          className="flex items-center gap-1.5 text-[10px] text-gray-600 font-medium whitespace-nowrap"
                        >
                          <div
                            className="w-2.5 h-2.5 rounded-[2px]" 
                            style={{ backgroundColor: item.color }}
                          />
                          {item.location}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </DonutCard>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComplaintsOverview;